import React from "react";
import { format } from "date-fns";
import { X, MessageSquare, Clock } from "lucide-react";
import { IRepaymentLog } from "../../models/commissionAndRepayment.model";

interface Props {
  open: boolean;
  logs: IRepaymentLog[];
  onClose: () => void;
}

const RepaymentLogsModal: React.FC<Props> = ({ open, logs, onClose }) => {
  if (!open) return null;

  const sortedLogs = [...logs].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-100 to-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-sky-600" />
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  Repayment Logs
                </h2>
                <p className="text-xs text-gray-600">
                  History of rejected repayments
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 overflow-y-auto max-h-[calc(90vh-120px)]">
          {logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-56 text-center">
              <MessageSquare className="w-10 h-10 text-gray-300 mb-3" />
              <p className="text-gray-500 text-sm">No logs available</p>
            </div>
          ) : (
            <>
              <div className="bg-slate-50 rounded-xl p-4 mb-6 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">Total Rejections</p>
                  <p className="text-lg font-semibold text-slate-800">
                    {logs.length}
                  </p>
                </div>

                <div className="bg-white shadow-sm px-3 py-1 rounded-lg text-xs text-gray-500">
                  Latest update shown first
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-6 top-0 bottom-0 w-[2px] bg-gray-200"></div>

                {sortedLogs.map((log, index) => {
                  const isLatest = index === 0;

                  return (
                    <div key={index} className="relative mb-6 last:mb-0">
                      <div
                        className={`absolute left-4 w-4 h-4 rounded-full border-4 bg-white ${
                          isLatest
                            ? "border-sky-500 shadow-md"
                            : "border-gray-300"
                        }`}
                      />

                      <div className="ml-12">
                        <div
                          className={`border rounded-xl p-4 transition hover:shadow-md ${
                            isLatest
                              ? "border-sky-200 bg-sky-50"
                              : "border-gray-200 bg-white"
                          }`}
                        >
                          <p className="text-sm text-gray-800 font-medium">
                            {log.remark}
                          </p>

                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
                            <Clock className="w-3 h-3" />
                            {format(
                              new Date(log.createdAt),
                              "dd MMM yyyy, hh:mm a",
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RepaymentLogsModal;
